import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Chip, Tooltip, Alert } from '@mui/material';
import { useAppSelector } from '@common-reducers/hooks/store.hook';
import { SessionStatusPill } from '../../components/session-status-pill';
import { TimeAgo } from '../../components/time-ago';
import { CopyableText } from '../../components/copyable-text';
import { Info, Cloud, Smartphone, AlertTriangle } from 'lucide-react';

interface SessionsTabProps {
    supportedUserId: string;
}

const formatTimestamp = (timestamp: string | Date | undefined): string => {
    if (!timestamp) return '';
    if (timestamp instanceof Date) {
        return timestamp.toISOString();
    }
    return timestamp;
};

// Extract phone number from WhatsApp ID (e.g., "972559811714@c.us" -> "+972559811714")
const formatWhatsAppPhone = (whatsappId: string | undefined): string => {
    if (!whatsappId) return '-';
    const match = whatsappId.match(/^(\d+)@/);
    if (match) {
        return `+${match[1]}`;
    }
    return whatsappId;
};

// Get short pod name (e.g., "session-6968e61893ae1907512f1bd7" -> "6968e61...")
const formatPodName = (podId: string | undefined): string => {
    if (!podId) return '-';
    // Remove "session-" prefix if present
    const cleanId = podId.replace(/^session-/, '');
    return cleanId.substring(0, 8) + '...';
};

export function SessionsTab({ supportedUserId }: SessionsTabProps) {
    const sessions = useAppSelector((state) =>
        Object.values(state.DBSessionReducer.entities)
            .filter((session): session is NonNullable<typeof session> => !!session && session.owner === supportedUserId)
            .sort((a, b) => new Date(b.updatedAt ?? 0).getTime() - new Date(a.updatedAt ?? 0).getTime())
    );

    const activeSessions = sessions.filter((s) => s.status === 'connected');
    const hasMultipleActive = activeSessions.length > 1;

    return (
        <Box>
            {/* Explanation Header */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Info size={16} className="text-blue-500" />
                    <Typography variant="subtitle2" fontWeight="bold">
                        What are Gateway Sessions?
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    Gateway sessions are WhatsApp connections running in our cloud infrastructure.
                    Each session represents an active WhatsApp Web instance that can send messages even when the user's computer is off
                    or the browser is closed.
                </Typography>
            </Box>

            {/* Warning if multiple active sessions */}
            {hasMultipleActive && (
                <Alert severity="warning" sx={{ mb: 3 }} icon={<AlertTriangle size={20} />}>
                    <Typography variant="body2">
                        <strong>Multiple active sessions detected!</strong> This user has {activeSessions.length} connected sessions.
                        This may cause duplicate message sends. Consider terminating extra sessions.
                    </Typography>
                </Alert>
            )}

            {/* Summary Stats */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Paper sx={{ p: 2, flex: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Total Sessions
                    </Typography>
                    <Typography variant="h4">{sessions.length}</Typography>
                </Paper>
                <Paper sx={{ p: 2, flex: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Active Now
                    </Typography>
                    <Typography variant="h4" color={activeSessions.length > 0 ? 'success.main' : 'text.secondary'}>
                        {activeSessions.length}
                    </Typography>
                </Paper>
            </Box>

            {/* Sessions Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell>
                                <Tooltip title="Current connection status of the gateway session">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        Status
                                        <Info size={14} className="text-gray-400" />
                                    </Box>
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                <Tooltip title="The WhatsApp phone number linked to this session">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Smartphone size={14} />
                                        WhatsApp Phone
                                    </Box>
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                <Tooltip title="The Kubernetes pod running this WhatsApp session in our cloud">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Cloud size={14} />
                                        Cloud Pod
                                    </Box>
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                <Tooltip title="Number of times this session failed to connect">
                                    <span>Connection Failures</span>
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                <Tooltip title="Last time this session reported being online">
                                    <span>Last Active</span>
                                </Tooltip>
                            </TableCell>
                            <TableCell>
                                <Tooltip title="When this session was first created">
                                    <span>Created</span>
                                </Tooltip>
                            </TableCell>
                            <TableCell>Session ID</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sessions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No gateway sessions found for this user.
                                        <br />
                                        Sessions are created when the user enables "Offline Mode" or uses the Gateway feature.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            sessions.map((session, index) => (
                                <TableRow
                                    key={session._id}
                                    sx={{
                                        bgcolor: index === 0 ? 'action.hover' : 'inherit',
                                        '&:hover': { bgcolor: 'action.selected' }
                                    }}
                                >
                                    <TableCell>
                                        <SessionStatusPill status={session.status} />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontFamily="monospace">
                                            {formatWhatsAppPhone(session.myWId)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={session.podId || 'No pod assigned'}>
                                            <Chip
                                                label={formatPodName(session.podId)}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        {(session.failedAttempts ?? 0) > 0 ? (
                                            <Chip
                                                label={session.failedAttempts}
                                                size="small"
                                                color="error"
                                                variant="outlined"
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">0</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <TimeAgo timestamp={formatTimestamp(session.lastSeen)} />
                                    </TableCell>
                                    <TableCell>
                                        <TimeAgo timestamp={formatTimestamp(session.createdAt)} />
                                    </TableCell>
                                    <TableCell>
                                        <CopyableText text={session._id ?? ''} />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
